import {
  BoltIcon,
  ClockIcon,
  DatesDisplay,
  Flex,
  LocationDisplay,
  Panel,
  StatusBadge,
  Text,
  UserAvatar,
} from 'components'
import { generatePath, useNavigate } from 'react-router'
import { useUser, useWorkspaceRecord } from 'utils'
import { EventRecord, getDaysAway } from '../../../utils'
import { RoutePath } from '../../../utils/routing'

interface Props extends EventRecord {
  isLive?: boolean
  isUpcoming?: boolean
  showWorkspace: boolean
}

export default function EventListCard(event: Props) {
  const navigate = useNavigate()
  const workspace = useWorkspaceRecord(event.workspaceId)
  const eventManager = useUser(event?.managerId)
  const daysAway = getDaysAway(event.startTime!)
  const sessionCount = event.sessionCount ?? 0

  function goToEvent(id: string, workspaceId: string) {
    navigate(generatePath(RoutePath.EventInfo, { workspaceId: workspaceId!, id }))
  }

  return (
    <Panel
      w="100%"
      h="70px"
      p={5}
      gap={4}
      align="center"
      border={event.isLive ? '1px solid  rgba(30, 166, 68, 1)' : undefined}
      boxShadow={
        event.isLive
          ? '0px 1px 4px 0px rgba(30, 166, 68, 0.7)'
          : '0px 1px 4px 0px rgba(0, 0, 0, 0.06)'
      }
      direction="row"
      cursor="pointer"
      onClick={() => goToEvent(event.id, event.workspaceId)}
    >
      {event.isLive && <StatusBadge text="Live" iconVisible={true} />}
      <Flex direction="column" w={event.isLive ? '28%' : '35%'} overflow="hidden">
        <Text {...textProps} textStyle="bodyMedium">
          {event.title}
        </Text>
        {event.showWorkspace && (
          <Text {...textProps} textStyle="bodyXSmall" opacity={0.7}>
            {workspace?.title}
          </Text>
        )}
      </Flex>
      <DatesDisplay startTime={event.startTime} endTime={event.endTime} w="20%" />
      <LocationDisplay location={event.location} containerProps={{ w: '20%' }} />

      {!event.showWorkspace && (
        <Flex align="center" gap={2} w="20%">
          <UserAvatar user={eventManager} />
          <Text {...textProps}>{eventManager?.displayName}</Text>
        </Flex>
      )}

      <Flex direction="row" alignItems="center" gap={2} w="20%">
        {event.isUpcoming ? (
          <>
            <ClockIcon type="outline" />
            <Text {...textProps}>{daysAway}</Text>
          </>
        ) : (
          <>
            <BoltIcon size="sm" />
            <Text {...textProps}>
              {sessionCount.toLocaleString()} {sessionCount === 1 ? 'Session' : 'Sessions'}
            </Text>
          </>
        )}
      </Flex>
    </Panel>
  )
}

const textProps = {
  textStyle: 'bodySmall',
  truncate: true,
  textOverflow: 'ellipsis',
}
